import { useEffect, useRef, useState, type ReactNode } from "react";
import { Button, PocketSection } from "@/components/ui";
import { cn } from "@/lib/cn";
import { useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { settingsBrowserMessages } from "@/i18n/messages/settings-browser";
import {
  BROWSERS,
  browserConnectionApplies,
  listBrowserConnections,
  isMissingBrowserCatalog,
  type BrowserConnectionStatus,
  type BrowserId,
} from "@/desktop/browser-connection";
import { browserLaneApplies } from "@/desktop/denker-browser";
import { useBrowserExtensionSetupEvents } from "@/desktop/browser-extension-setup";
import { useBrowserExtensions } from "@/desktop/use-browser-extensions";
import { BrowserConnectRow } from "./browser-connect-flow";
import { ChromiumBrowserRow } from "./browser-chromium-row";
import { BrowserLaneRows } from "./browser-lane-rows";
import { BrowserPopupsRow } from "./browser-popups-row";
import { BrowserConnectionTroubleshooting } from "./browser-connection-troubleshooting";
import { useBrowserConnectionSession } from "@/desktop/browser-connection-session";
import { useSettingsNav } from "./settings-navigation";

const RING_MS = 2_000;

/** A browser's row; the one the page was opened for (an agent's "open
 * Connections on Chrome") scrolls into view and is ringed for a moment. */
function BrowserRowFrame({
  browserId,
  requested,
  children,
}: {
  browserId: BrowserId;
  requested: boolean;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [ringed, setRinged] = useState(false);
  useEffect(() => {
    if (!requested) return;
    ref.current?.scrollIntoView({ block: "nearest" });
    setRinged(true);
    const timer = setTimeout(() => setRinged(false), RING_MS);
    return () => {
      clearTimeout(timer);
      setRinged(false);
    };
  }, [requested]);
  return (
    <div
      ref={ref}
      data-testid={`browser-row-${browserId}`}
      className={cn(
        "rounded-[16px] transition-shadow duration-300",
        ringed && "ring-2 ring-inset ring-accent",
      )}
    >
      {children}
    </div>
  );
}

export function BrowserConnectionsSection() {
  const t = useT(settingsBrowserMessages);
  const tCommon = useT(commonMessages);
  // The lane and the extension cover every platform; Apple Events are
  // macOS-only, Safari connected by them alone. Only installed browsers
  // have a row: never the whole catalog.
  const laneApplies = browserLaneApplies();
  const applies = browserConnectionApplies();
  const { extensions, refresh } = useBrowserExtensions(laneApplies);
  useBrowserExtensionSetupEvents();
  const session = useBrowserConnectionSession((state) => state.session);
  const sessionBrowser = session?.browserId;
  const { stack } = useSettingsNav();
  const requestedBrowser = stack[stack.length - 1]?.args?.browser;
  const [browsers, setBrowsers] = useState<BrowserConnectionStatus[]>([]);
  const [error, setError] = useState<"unavailable" | "failed" | null>(null);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    if (!applies) return;
    let current = true;
    setError(null);
    void listBrowserConnections().then(
      (statuses) => {
        if (current) setBrowsers(statuses.filter((status) => status.installed));
      },
      (failure: unknown) => {
        if (current) {
          setBrowsers([]);
          setError(isMissingBrowserCatalog(failure) ? "unavailable" : "failed");
        }
      },
    );
    return () => {
      current = false;
    };
  }, [applies, revision]);
  if (!laneApplies) return null;
  const inventoried = new Set<BrowserId>([
    ...browsers.map(({ browser_id }) => browser_id),
    ...(sessionBrowser ? [sessionBrowser] : []),
    ...(error ? ["safari" as const] : []),
  ]);
  // A browser Apple Events reach is installed as their inventory says;
  // another - Chromium, or every browser off macOS - as its extension row.
  const browserIds = (Object.keys(BROWSERS) as BrowserId[]).filter(
    (browserId) =>
      applies && BROWSERS[browserId].appleEvents
        ? inventoried.has(browserId)
        : !!extensions?.[browserId],
  );
  return (
    <PocketSection title={t("sectionTitle")} testId="connections-browsers">
      <p className="px-2 pb-2 text-appkit-small text-muted">
        {t("sectionIntro")}
      </p>
      <BrowserLaneRows />
      <BrowserPopupsRow />
      {browserIds.map((browserId) => (
        <BrowserRowFrame
          key={browserId}
          browserId={browserId}
          requested={browserId === requestedBrowser}
        >
          {browserId === "safari" ? (
            <BrowserConnectRow browserId={browserId} />
          ) : (
            <ChromiumBrowserRow
              browserId={browserId}
              extension={extensions?.[browserId]}
              loaded={extensions !== null}
              refresh={refresh}
            />
          )}
        </BrowserRowFrame>
      ))}
      {error ? (
        <p role="alert" className="px-2 text-appkit-small text-muted">
          {error === "unavailable"
            ? t("catalogNeedsNewerBuild")
            : t("catalogReadFailed")}{" "}
          <Button
            size="sm"
            variant="ghost"
            data-testid="browser-connections-retry"
            onClick={() => setRevision((value) => value + 1)}
          >
            {tCommon("retry")}
          </Button>
        </p>
      ) : null}
      <BrowserConnectionTroubleshooting />
    </PocketSection>
  );
}
